import React, { useState, useEffect } from 'react';
import { getData, saveData, db } from '../firebase';
import { doc, deleteDoc } from 'firebase/firestore';
import { LEDGER_GROUPS, ACCOUNT_HEADS, DEFAULT_LEDGERS, headsForGroup, groupForHead, computeLedgerBalances, getLedgerEntries } from '../ledgers';

const BRAND = '#0f6e56';

export default function Ledgers({ user }) {
  const [data, setData] = useState(null);
  const [customLedgers, setCustomLedgers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedLedger, setSelectedLedger] = useState(null);
  const [groupBy, setGroupBy] = useState('head');
  const [form, setForm] = useState({ name: '', head: 'Bank Accounts', group: 'ASSET' });

  const load = async () => {
    setLoading(true);
    const [invoices, expenses, pettycash, cashbook, bankbook, journal, ledgers] = await Promise.all([
      getData(user.uid, 'invoices'), getData(user.uid, 'expenses'),
      getData(user.uid, 'pettycash'), getData(user.uid, 'cashbook'),
      getData(user.uid, 'bankbook'), getData(user.uid, 'journal'),
      getData(user.uid, 'ledgers'),
    ]);
    setData({ invoices, expenses, pettycash, cashbook, bankbook, journal });
    setCustomLedgers(ledgers.map(l => ({ ...l, custom: true })));
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const addLedger = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    const group = groupForHead(form.head);
    await saveData(user.uid, 'ledgers', { name: form.name.trim(), head: form.head, group });
    setShowForm(false);
    setForm({ name: '', head: 'Bank Accounts', group: 'ASSET' });
    load();
  };

  const delLedger = async (id) => {
    if (!window.confirm('Delete this custom ledger?')) return;
    await deleteDoc(doc(db, 'users', user.uid, 'ledgers', id));
    load();
  };

  const fmt = n => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#aaa' }}>Loading ledgers...</div>;

  const balances = computeLedgerBalances(data, customLedgers);

  // Drill-down view for a single ledger
  if (selectedLedger) {
    const entries = getLedgerEntries(data, selectedLedger);
    const totalDr = entries.reduce((s, e) => s + e.debit, 0);
    const totalCr = entries.reduce((s, e) => s + e.credit, 0);
    const led = balances[selectedLedger] || {};
    return (
      <div>
        <button onClick={() => setSelectedLedger(null)} style={{ background: 'none', border: 'none', color: BRAND, cursor: 'pointer', fontSize: 14, marginBottom: 16 }}>← Back to Ledger Master</button>
        <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 2 }}>{selectedLedger}</h2>
        <div style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>{led.head} · {LEDGER_GROUPS[led.group]?.label} · Balance: {fmt(led.balance)} {led.balanceSide === 'debit' ? 'Dr' : 'Cr'}</div>
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e8e8e8', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#f7f8fa' }}>
              {['Date', 'Ref', 'Narration', 'Contra ledger', 'Debit', 'Credit'].map(h => (
                <th key={h} style={{ padding: '11px 16px', textAlign: h === 'Debit' || h === 'Credit' ? 'right' : 'left', fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {entries.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>No transactions for this ledger yet</td></tr>
              ) : entries.map((e, i) => (
                <tr key={i} style={{ borderTop: '1px solid #f0f0f0' }}>
                  <td style={td}>{e.date || '—'}</td>
                  <td style={td}><span style={{ fontSize: 11, color: '#888' }}>{e.ref}</span></td>
                  <td style={td}>{e.narration}</td>
                  <td style={td}><span style={{ color: BRAND }}>{e.contra}</span></td>
                  <td style={{ ...td, textAlign: 'right', color: e.debit ? '#0f6e56' : '#ccc', fontWeight: e.debit ? 600 : 400 }}>{e.debit ? fmt(e.debit) : '—'}</td>
                  <td style={{ ...td, textAlign: 'right', color: e.credit ? '#a32d2d' : '#ccc', fontWeight: e.credit ? 600 : 400 }}>{e.credit ? fmt(e.credit) : '—'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr style={{ background: '#f7f8fa', borderTop: '2px solid #e8e8e8' }}>
              <td colSpan={4} style={{ ...td, fontWeight: 700 }}>Total</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#0f6e56' }}>{fmt(totalDr)}</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#a32d2d' }}>{fmt(totalCr)}</td>
            </tr></tfoot>
          </table>
        </div>
      </div>
    );
  }

  // Build the grouped tree: by account head OR by group
  const ledgerList = Object.values(balances);

  let sections = [];
  if (groupBy === 'head') {
    // Group by account head, ordered by their top-level group
    const order = ['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'];
    const usedHeads = [...new Set(ledgerList.map(l => l.head || 'Other'))];
    const headOrder = ACCOUNT_HEADS.map(h => h.head);
    usedHeads.sort((a, b) => {
      const ga = order.indexOf(groupForHead(a)), gb = order.indexOf(groupForHead(b));
      if (ga !== gb) return ga - gb;
      return headOrder.indexOf(a) - headOrder.indexOf(b);
    });
    sections = usedHeads.map(head => ({
      title: head,
      group: groupForHead(head),
      items: ledgerList.filter(l => (l.head || 'Other') === head),
    }));
  } else {
    sections = Object.entries(LEDGER_GROUPS).map(([key, g]) => ({
      title: g.label,
      group: key,
      items: ledgerList.filter(l => l.group === key),
    }));
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700 }}>Ledger Master</h2>
        <div style={{ display: 'flex', gap: 10 }}>
          <select value={groupBy} onChange={e => setGroupBy(e.target.value)} style={{ ...inp, width: 'auto', padding: '8px 12px' }}>
            <option value="head">Group by account head</option>
            <option value="group">Group by type</option>
          </select>
          <button onClick={() => setShowForm(true)} style={{ padding: '10px 20px', background: BRAND, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>+ Add Ledger</button>
        </div>
      </div>
      <div style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>Chart of accounts — balances auto-calculated from all your transactions</div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {sections.filter(s => s.items.length > 0).map(section => {
          const g = LEDGER_GROUPS[section.group];
          return (
            <div key={section.title} style={{ background: '#fff', borderRadius: 14, border: '1px solid #e8e8e8', overflow: 'hidden' }}>
              <div style={{ background: g.bg, padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: g.color }}>{section.title}</span>
                  {groupBy === 'head' && <span style={{ fontSize: 11, color: g.color, opacity: 0.7 }}>({g.label})</span>}
                </div>
                <span style={{ fontSize: 12, color: g.color }}>{section.items.length} ledger{section.items.length !== 1 ? 's' : ''}</span>
              </div>
              {section.items.map(l => (
                <div key={l.name} onClick={() => setSelectedLedger(l.name)} style={{ padding: '12px 20px', borderTop: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: 14, fontWeight: 500 }}>{l.name}</span>
                    {l.custom && <span style={{ marginLeft: 8, fontSize: 10, background: '#e1f5ee', color: BRAND, padding: '2px 8px', borderRadius: 10 }}>Custom</span>}
                    {groupBy === 'group' && l.head && <span style={{ marginLeft: 8, fontSize: 11, color: '#aaa' }}>· {l.head}</span>}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: g.color }}>{fmt(l.balance)}</div>
                    <div style={{ fontSize: 11, color: '#aaa' }}>{l.balanceSide === 'debit' ? 'Dr' : 'Cr'}</div>
                  </div>
                  <span style={{ color: '#ccc' }}>›</span>
                  {l.custom && <button onClick={(ev) => { ev.stopPropagation(); delLedger(l.id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e24b4a', fontSize: 14 }}>🗑</button>}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: '100%', maxWidth: 440 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>Create New Ledger</div>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888' }}>×</button>
            </div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 20 }}>Example: name "SBI Bank" under account head "Bank Accounts"</div>
            <form onSubmit={addLedger}>
              <div style={{ marginBottom: 16 }}>
                <label style={lbl}>Ledger name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. SBI Bank, ABC Suppliers, GST Payable" style={inp} autoFocus />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={lbl}>Under account head *</label>
                <select value={form.head} onChange={e => setForm(f => ({ ...f, head: e.target.value, group: groupForHead(e.target.value) }))} style={inp}>
                  {Object.entries(LEDGER_GROUPS).map(([gKey, g]) => (
                    <optgroup key={gKey} label={g.label}>
                      {headsForGroup(gKey).map(h => <option key={h.head} value={h.head}>{h.head}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div style={{ background: '#e1f5ee', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#085041', marginBottom: 20 }}>
                This ledger will appear under <strong>{form.head}</strong> in <strong>{LEDGER_GROUPS[groupForHead(form.head)]?.label}</strong>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" onClick={() => setShowForm(false)} style={{ flex: 1, padding: 12, borderRadius: 10, border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" style={{ flex: 2, padding: 12, background: BRAND, color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}>Save Ledger</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Reusable ledger dropdown for other screens — grouped by account head
export function LedgerDropdown({ ledgers, value, onChange, placeholder = 'Select ledger...' }) {
  const order = ['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'];
  const heads = [...new Set(ledgers.map(l => l.head || 'Other'))];
  const headOrder = ACCOUNT_HEADS.map(h => h.head);
  heads.sort((a, b) => {
    const ga = order.indexOf(groupForHead(a)), gb = order.indexOf(groupForHead(b));
    if (ga !== gb) return ga - gb;
    return headOrder.indexOf(a) - headOrder.indexOf(b);
  });
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={inp}>
      <option value="">{placeholder}</option>
      {heads.map(head => (
        <optgroup key={head} label={head}>
          {ledgers.filter(l => (l.head || 'Other') === head).map(l => <option key={l.name} value={l.name}>{l.name}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

const td = { padding: '12px 16px', fontSize: 13, color: '#444' };
const lbl = { display: 'block', fontSize: 13, fontWeight: 500, color: '#555', marginBottom: 6 };
const inp = { width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: 10, fontSize: 14, color: '#222', outline: 'none', background: '#fff' };
